import {
  parseMaterialKey,
  getVid,
  // splitAndTrim, // not needed in this module
  includesWorkticket,
  collectWorkticketCounts,
  normalizeToSet,
  allocateForWorkticket,
  collectDeliveryNumbers,
  deriveAddressContact,
  buildMaterialObject,
  mergeEntries,
  renumberMaterialLines,
} from "./useReturnPayloadUtils";

// Index sample bundles by composite material key for O(1) lookups
const indexBundlesByKey = (sampleBundles = []) => {
  const map = new Map();
  sampleBundles.forEach((rec) => {
    const key = `${rec.hardwareId}|${rec.subscriptionId}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(rec);
  });
  return map;
};

const getRelatedForKey = (bundlesByKey, hardwareId, subscriptionId) => bundlesByKey.get(`${hardwareId}|${subscriptionId}`) || [];

const getVehicleTechRefsForMaterial = (veh, hardwareId, subscriptionId, wtId) => {
  const refs = new Set();
  if (!Array.isArray(veh?.materialMapping)) return refs;
  veh.materialMapping.forEach((m) => {
    if (!m) return;
    if (String(m.hardwareId) !== String(hardwareId)) return;
    if (String(m.subscriptionId) !== String(subscriptionId)) return;
    if (!includesWorkticket(m.workticketId, wtId)) return;
    refs.add(String(m.techRefId || ""));
  });
  return refs;
};

const groupRecordsByTechRef = (records = [], fallbackTechRefId = "") => {
  const grouped = new Map();
  records.forEach((rec) => {
    const key = String(rec?.techRefId || fallbackTechRefId || "");
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key).push(rec);
  });
  return grouped;
};

const allocateVehiclesForTechRef = ({ vehiclesPool, needed, techRefId, hardwareId, subscriptionId, wtId }) => {
  if (!Array.isArray(vehiclesPool) || vehiclesPool.length === 0 || needed <= 0) {
    return { chosen: [], remaining: Array.isArray(vehiclesPool) ? vehiclesPool : [] };
  }

  const matching = [];
  const nonMatching = [];

  vehiclesPool.forEach((veh) => {
    const refs = getVehicleTechRefsForMaterial(veh, hardwareId, subscriptionId, wtId);
    if (refs.has(String(techRefId || ""))) {
      matching.push(veh);
    } else {
      nonMatching.push(veh);
    }
  });

  const chosen = matching.slice(0, needed);
  const stillNeeded = needed - chosen.length;
  if (stillNeeded > 0) {
    chosen.push(...nonMatching.slice(0, stillNeeded));
  }

  const used = new Set(chosen);
  const remaining = vehiclesPool.filter((veh) => !used.has(veh));

  return { chosen, remaining };
};

// Process a single materialKey into zero or more return entries
const processMaterialKey = ({
  materialKey,
  savedMaterialKeys,
  materialsByKey,
  selectedVehicleEntities,
  bundlesByKey,
  disabledSet,
  getReturnCode,
  nextTempId,
  disabledEsnObjects,
}) => {
  if (!savedMaterialKeys[materialKey]) return [];

  const { hardwareId, subscriptionId } = parseMaterialKey(materialKey);
  const materialMeta = materialsByKey[materialKey];
  const relatedRecords = getRelatedForKey(bundlesByKey, hardwareId, subscriptionId);
  const selectedVehs = selectedVehicleEntities[materialKey] || [];
  if (!relatedRecords.length || !selectedVehs.length) return [];

  const workticketCounts = collectWorkticketCounts(relatedRecords);
  const workticketIds = Object.keys(workticketCounts);
  if (!workticketIds.length) return [];

  let vehiclesPool = [...selectedVehs];
  const entries = [];

  workticketIds.forEach((wtId) => {
    const needed = workticketCounts[wtId] || 0;
    const { chosen: allocatedVehs, remaining } = allocateForWorkticket(vehiclesPool, hardwareId, subscriptionId, wtId, needed, disabledSet);
    vehiclesPool = remaining;
    if (!allocatedVehs.length) return;

    const subsetRecords = relatedRecords.filter((r) => includesWorkticket(r.workTicketId, wtId));
    const oldDeliveryNo = collectDeliveryNumbers(subsetRecords);
    const exemplar = subsetRecords[0] || relatedRecords[0] || {};
    const returnProcessingCode = getReturnCode(exemplar.processingCode || materialMeta?.processingCode || "");
    const { derivedAddressId, derivedContactId } = deriveAddressContact(allocatedVehs, subsetRecords);

    const groupedByTechRef = groupRecordsByTechRef(subsetRecords, subscriptionId);
    let groupedVehiclesPool = [...allocatedVehs];
    const materialObjects = [];

    groupedByTechRef.forEach((recordsForTechRef, techRefId) => {
      const { chosen, remaining } = allocateVehiclesForTechRef({
        vehiclesPool: groupedVehiclesPool,
        needed: recordsForTechRef.length,
        techRefId,
        hardwareId,
        subscriptionId,
        wtId,
      });
      groupedVehiclesPool = remaining;

      const materialObj = buildMaterialObject(
        recordsForTechRef[0] || exemplar,
        hardwareId,
        subscriptionId,
        recordsForTechRef,
        chosen,
        returnProcessingCode,
        wtId,
        nextTempId,
        disabledEsnObjects,
        techRefId
      );

      materialObjects.push(materialObj);
    });

    entries.push({
      workticketId: wtId,
      oldDeliveryNo,
      addressId: derivedAddressId,
      contactId: derivedContactId,
      materials: materialObjects,
    });
  });

  return entries;
};

export function useReturnPayload() {
  const buildReturnPayload = ({
    savedMaterialKeys,
    materialsByKey,
    sampleBundles,
    selectedVehicleEntities,
    getReturnCode,
    disabledKeys,
    disabledEsnObjects,
  }) => {
    let tempIdCounter = 0;
    const nextTempId = () => `temp-${++tempIdCounter}`;

    const disabledSet = normalizeToSet(disabledKeys);

    const bundlesByKey = indexBundlesByKey(sampleBundles);

    const returnsEntries = Object.keys(savedMaterialKeys).flatMap((materialKey) =>
      processMaterialKey({
        materialKey,
        savedMaterialKeys,
        materialsByKey,
        selectedVehicleEntities,
        bundlesByKey,
        disabledSet,
        getReturnCode,
        nextTempId,
        disabledEsnObjects,
      })
    );
    const mergedReturns = renumberMaterialLines(mergeEntries(returnsEntries));
    // // If specific ESNs were disabled in the summary, remove them from payload
    // if (Array.isArray(disabledEsnObjects) && disabledEsnObjects.length > 0) {
    //   mergedReturns.forEach((ret) => {
    //     const wtId = String(ret.workticketId || "");
    //     if (!Array.isArray(ret.materials)) return;
    //     ret.materials.forEach((mat) => {
    //       const matId = String(mat.materialId || "");
    //       const matches = disabledEsnObjects.filter((d) => String(d.workticketId || "") === wtId && String(d.hardwareId || "") === matId);
    //       if (!matches.length || !Array.isArray(mat.esnTobeReturned)) return;
    //       matches.forEach(({ esn }) => {
    //         mat.esnTobeReturned = mat.esnTobeReturned.filter((e) => String(e.esn) !== String(esn) && String(e.esnId) !== String(esn));
    //       });
    //     });
    //   });
    // }
    return { returns: mergedReturns };
  };

  return { buildReturnPayload };
}

export function useSummaryGrouping() {
  // Create a stable, primitive key for an address object
  const getAddressKey = (obj = {}) => {
    if (!obj || typeof obj !== "object") return "";
    if (obj.addressId) return String(obj.addressId);
    if (obj.address1) return String(obj.address1);
    const parts = [
      obj.address1 || obj.address || "",
      obj.address2 || "",
      obj.city || obj.cityName || "",
      obj.stateCode || obj.state || "",
      obj.zipcode || obj.zip || "",
      obj.countryCode || obj.country || "",
    ];
    return parts.join("|");
  };

  const buildSummaryGrouping = ({ savedMaterialKeys, materialVehiclesCache, selectedVehicleEntities }) => {
    const selectedIdSet = new Set();
    Object.keys(savedMaterialKeys).forEach((mk) => {
      if (!savedMaterialKeys[mk]) return;
      (selectedVehicleEntities?.[mk] || []).forEach((v) => {
        const vid = getVid(v);
        if (vid) selectedIdSet.add(vid);
      });
    });

    const mergedByBundle = {};
    const addVehicleToGroup = (bundleId, addressObj, contactObj, veh) => {
      const addrObj = addressObj || {};
      const contObj = contactObj || {};
      const vid = getVid(veh);
      if (!vid || !selectedIdSet.has(vid)) return;
      if (!mergedByBundle[bundleId]) mergedByBundle[bundleId] = new Map();
      const addrKey = getAddressKey(addrObj);
      if (!mergedByBundle[bundleId].has(addrKey)) {
        mergedByBundle[bundleId].set(addrKey, { address: addrObj, contact: contObj, vehicles: [], _seen: new Set() });
      }
      const group = mergedByBundle[bundleId].get(addrKey);
      if (!group._seen.has(vid)) {
        group._seen.add(vid);
        group.vehicles.push(veh);
      }
    };

    Object.keys(savedMaterialKeys).forEach((mk) => {
      if (!savedMaterialKeys[mk]) return;
      const src = materialVehiclesCache[mk] || {};
      Object.entries(src).forEach(([bundleId, groupsArr]) => {
        if (!Array.isArray(groupsArr)) return;
        groupsArr.forEach((g) => {
          const addr = g?.address || {};
          const contact = g?.contact || {};
          (g?.vehicles || []).forEach((veh) => addVehicleToGroup(bundleId, addr, contact, veh));
        });
      });
    });

    const summaryMap = {};
    Object.entries(mergedByBundle).forEach(([bundleId, addrMap]) => {
      summaryMap[bundleId] = Array.from(addrMap.values()).map((g) => ({ address: g.address, contact: g.contact, vehicles: g.vehicles }));
    });

    return summaryMap;
  };

  return { buildSummaryGrouping };
}
